import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HeartIcon,
  ArrowPathIcon,
  ShoppingBagIcon,
  ClockIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import MessageDisplay from './MessageDisplay';

const API_BASE = 'https://mongobyte-hc73.onrender.com/api/v1';

const QuickReorderDashboard = ({ userToken, onOrderSuccess, onViewAll }) => {
  const [quickReorders, setQuickReorders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = useCallback((type, title, content, details = null) => {
    setMessage({ type, title, content, details });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  // Fetch quick reorders
  const fetchQuickReorders = useCallback(async () => {
    if (!userToken) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/advanced-orders/quick-reorders`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Sort by last ordered date and times ordered
        const sorted = (result.data || [])
          .sort((a, b) => {
            const aLastOrdered = new Date(a.lastOrdered || 0);
            const bLastOrdered = new Date(b.lastOrdered || 0);
            const aTimesOrdered = a.timesOrdered || 0;
            const bTimesOrdered = b.timesOrdered || 0;
            
            // Prioritize frequently used orders
            if (aTimesOrdered !== bTimesOrdered) {
              return bTimesOrdered - aTimesOrdered;
            }
            
            // Then by last ordered date
            return bLastOrdered - aLastOrdered;
          })
          .slice(0, 4); // Show top 4
          
        setQuickReorders(sorted);
      }
    } catch (error) {
      console.error('Error fetching quick reorders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userToken]);

  // Execute quick reorder
  const executeQuickReorder = async (reorderId, orderName) => {
    setIsExecuting(true);
    try {
      const response = await fetch(
        `${API_BASE}/advanced-orders/quick-reorder/${reorderId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`
          },
          body: JSON.stringify({}) // Empty body uses profile defaults
        }
      );
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        const order = result.data?.order;
        const quickReorderUsed = result.data?.quickReorderUsed;
        const pricing = result.data?.pricing;
        
        // Create detailed success message with order details
        const mealNames = order?.meals?.map(m => m.meal?.name).join(', ') || 'items';
        const restaurant = order?.restaurant?.name || 'restaurant';
        const totalPrice = pricing?.finalPrice || order?.totalPrice || 0;
        
        showMessage(
          'success',
          '🎉 Order Placed Successfully!',
          `${mealNames} from ${restaurant}`,
          `Order #${order?.customId} • Total: ₦${totalPrice.toLocaleString()} • Status: ${order?.status}`
        );
        
        if (onOrderSuccess) {
          onOrderSuccess(order);
        }
        
        // Refresh the list to update usage counts
        fetchQuickReorders();
      } else {
        throw new Error(result.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error executing quick reorder:', error);
      
      if (error.message.includes('insufficient balance')) {
        showMessage('error', 'Insufficient Balance', 'Please fund your wallet and try again');
      } else if (error.message.includes('unavailable')) {
        showMessage('error', 'Items Unavailable', 'Some items are no longer available');
      } else {
        showMessage('error', 'Order Failed', error.message || 'Failed to place order');
      }
    } finally {
      setIsExecuting(false);
    }
  };

  useEffect(() => {
    fetchQuickReorders();
  }, [fetchQuickReorders]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-100">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
          <span className="ml-3 text-gray-600">Loading favorites...</span>
        </div>
      </div>
    );
  }

  if (!quickReorders || quickReorders.length === 0) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <HeartSolid className="h-6 w-6 text-red-500" />
            <h3 className="text-lg font-bold text-gray-900">Quick Reorder</h3>
          </div>
        </div>
        
        <div className="text-center py-8">
          <HeartIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h4 className="font-semibold text-gray-700 mb-2">No Quick Reorders Yet</h4>
          <p className="text-gray-500 text-sm mb-4">
            Save your favorite orders for instant reordering
          </p>
          <div className="bg-white/50 p-4 rounded-lg">
            <h5 className="font-medium text-gray-700 mb-2">How to save quick reorders:</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Place an order and complete it</li>
              <li>• Go to Order History</li>
              <li>• Click "Save for Quick Reorder"</li>
              <li>• Instantly reorder with one click!</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <HeartSolid className="h-6 w-6 text-red-500" />
          <h3 className="text-lg font-bold text-gray-900">Quick Reorder</h3>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
          >
            <EyeIcon className="h-4 w-4" />
            View All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence>
          {quickReorders.map((order, index) => (
            <motion.div
              key={order._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg p-4 border border-gray-200 hover:border-red-200 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">
                      {order.name}
                    </h4>
                    {(order.timesOrdered || 0) > 5 && (
                      <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                    <span className="flex items-center gap-1">
                      <ShoppingBagIcon className="h-3 w-3" />
                      {order.restaurant?.name}
                    </span>
                    <span>•</span>
                    <span>₦{order.totalPrice?.toLocaleString()}</span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <ArrowPathIcon className="h-3 w-3" />
                      {order.timesOrdered || 0}x ordered
                    </span>
                    {order.lastOrdered && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-3 w-3" />
                          {new Date(order.lastOrdered).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 mb-3">
                    {order.meals?.slice(0, 2).map((item, index) => (
                      <span key={index}>
                        {item.quantity}x {item.meal?.name}
                        {index < Math.min(order.meals.length - 1, 1) && ', '}
                      </span>
                    ))}
                    {order.meals?.length > 2 && (
                      <span className="text-gray-500">
                        {' '}+{order.meals.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => executeQuickReorder(order._id, order.name)}
                disabled={isExecuting}
                className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
                  isExecuting
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {isExecuting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    <span>Ordering...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Reorder Now</span>
                  </div>
                )}
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="mt-6 p-4 bg-white/50 rounded-lg">
        <h5 className="font-medium text-gray-700 mb-2">💡 Quick Reorder Tips:</h5>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• One-click ordering with your saved favorites</li>
          <li>• Automatically uses your current profile info</li>
          <li>• Update delivery details if needed during reorder</li>
          <li>• Track your most popular combinations</li>
        </ul>
      </div>

      <MessageDisplay message={message} onClose={() => setMessage(null)} />
    </div>
  );
};

export default QuickReorderDashboard;
